import React, { useContext, useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { CodeTypeEnum, LovCompanyCodeListDTO } from '@api/ringil-api.v2.tsx';
import { ApiContext } from '@api/api.tsx';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { toTestIdSlug } from '@utils/testUtils';
import { formatCompanyCodeListLabel } from '@utils/formators';

interface ComponentProps {
  secured?: SecuredConfig;
  type: CodeTypeEnum;
  value: string;
  onChange: (id: string) => void;
  readonly?: boolean;
  inheritorId?: string;
}

const InternalInfoCatalogTag: React.FC<ComponentProps> = ({ secured, type, value, onChange, readonly, inheritorId }) => {
  const { tr } = useTranslationLgs();
  const {
    lovCompanyCodeList,
    lovOnBehalfOfForInheritor,
  } = useContext(ApiContext);

  const [internalValue, setInternalValue] = useState<string>(value);
  const [options, setOptions] = useState<LovCompanyCodeListDTO[]>([]);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (type === CodeTypeEnum.ON_BEHALF_OF) {
      if (!inheritorId) {
        setOptions([]);
        return;
      }
      lovOnBehalfOfForInheritor(inheritorId, data => setOptions(data));
      return;
    }
    lovCompanyCodeList(
      type,
      '',
      data => setOptions(data),
    );
  }, [lovCompanyCodeList, lovOnBehalfOfForInheritor, type, inheritorId]);

  const renderText = (value: string) => {
    const item = options?.find(opt => opt.id === value);
    return item ? formatCompanyCodeListLabel(item) : undefined;
  };

  const label = (() => {
    switch (type) {
      case CodeTypeEnum.ACCOUNT:
        return tr('InternalInfoCatalogTag.accountLabel', 'Account');
      case CodeTypeEnum.COST_CENTER:
        return tr('InternalInfoCatalogTag.costCenterLabel', 'Cost Center');
      case CodeTypeEnum.COST_UNIT:
        return tr('InternalInfoCatalogTag.costUnitLabel', 'Cost Unit');
      case CodeTypeEnum.PROJECT:
        return tr('InternalInfoCatalogTag.projectLabel', 'Project');
      case CodeTypeEnum.ON_BEHALF_OF:
        return tr('InternalInfoCatalogTag.onBehalfOfLabel', 'On behalf of');
      default:
        return tr('InternalInfoCatalogTag.unknownLabel', 'Unknown');
    }
  })();

  const dataTestIdBase = (() => {
    switch (type) {
      case CodeTypeEnum.ACCOUNT:
        return 'internalInfo-account';
      case CodeTypeEnum.COST_CENTER:
        return 'internalInfo-costCenter';
      case CodeTypeEnum.COST_UNIT:
        return 'internalInfo-costUnit';
      case CodeTypeEnum.PROJECT:
        return 'internalInfo-project';
      case CodeTypeEnum.ON_BEHALF_OF:
        return 'internalInfo-onBehalfOf';
      default:
        return 'internalInfo-unknown';
    }
  })();

  const listTestId = `${dataTestIdBase}List`;

  const getOptionLabel = (option: LovCompanyCodeListDTO | null | undefined): string => formatCompanyCodeListLabel(option);

  const applyOverlayTestIds = () => {
    const panels = document.querySelectorAll('.p-dropdown-panel.catalogDropdownPanel');
    const panel = panels.length ? (panels[panels.length - 1] as HTMLElement) : null;
    if (!panel) return;

    panel.setAttribute('data-testid', `${listTestId}-overlay`);
  };

  return (
    <>
      <ButtonTag secured={secured}
        label={label}
        value={renderText(internalValue)}
        onSave={(onSuccess) => {
          onChange(internalValue);
          onSuccess();
        }}
        readonly={readonly}
        autoClose
        dataTestId={dataTestIdBase}
      >
        <div data-testid={listTestId}>
          <Dropdown
            value={internalValue}
            options={options}
            onChange={(e) => setInternalValue(e.value)}
            optionLabel="name"
            optionValue="id"
            panelClassName="catalogDropdownPanel"
            itemTemplate={(option) => (
              <div data-testid={`${listTestId}-${toTestIdSlug(getOptionLabel(option))}`}>
                <span>{getOptionLabel(option)}</span>
              </div>
            )}
            valueTemplate={(option) =>
              option ? <span>{getOptionLabel(option)}</span> : <span>{tr('InternalInfoCatalogTag.placeholder', 'Select value')}</span>
            }
            onShow={() => {
              requestAnimationFrame(applyOverlayTestIds);
            }}
            placeholder={tr('InternalInfoCatalogTag.selectPlaceholder', 'Select a value')}
            className="w-full"
            filter
            filterBy="name,externalId"
            showClear
          />
        </div>
      </ButtonTag>
    </>);

};

export default InternalInfoCatalogTag;